import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  ModalV2,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useCancelSectionMutation } from '~/store/services/groupClass';

const TOP_LEVEL_ID = 'group-section-cancel-modal';

interface CancelSectionModalProps {
  groupId: string;
  isOpen: boolean;
  onClose: () => void;
  sectionExternalId: string;
  onSuccess: () => void;
}

const CancelSectionModal = ({
  groupId,
  isOpen,
  onClose,
  sectionExternalId,
  onSuccess,
}: CancelSectionModalProps) => {
  const { trackEvent } = useAnalytics();
  const [cancelSection, { isLoading: isCancellingSection }] =
    useCancelSectionMutation();

  const { createToast } = useToastMessage();

  const handleConfirmCancel = () => {
    const showToast = (success: boolean) => {
      createToast({
        icon: success
          ? FAIconName.checkRegular
          : FAIconName.exclamationTriangleRegular,
        id: `section-canceled-${success ? 'success' : 'error'}`,
        text: success
          ? 'Section canceled successfully'
          : 'Error canceling section',
        variant: success ? 'success' : 'critical',
      });
    };

    trackEvent(GroupSectionEvents.cancelSectionConfirmButtonClicked, {
      Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
    });

    cancelSection({ groupId, sectionExternalId })
      .unwrap()
      .then(() => {
        showToast(true);
        onSuccess();
      })
      .catch(() => {
        showToast(false);
      })
      .finally(() => {
        onClose();
      });
  };

  const renderFooter = () => (
    <ChakraFlex justifyContent="space-between" marginTop="24px" width="100%">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__back-button`}
        leftIcon={FAIconName.arrowLeftRegular}
        onClick={() => {
          onClose();
          trackEvent(GroupSectionEvents.cancelSectionBackButtonClicked, {
            Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
          });
        }}
        variant="secondary"
      >
        Back
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel-button`}
        leftIcon={FAIconName.checkRegular}
        onClick={handleConfirmCancel}
        variant="accent.critical"
        isLoading={isCancellingSection}
      >
        Cancel section
      </FireflyButton>
    </ChakraFlex>
  );

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Cancel section"
      footerContent={renderFooter()}
    >
      <Text>
        Are you sure you want to cancel this section? All registered users will
        be unregistered and have all future events removed from their schedule.
      </Text>
    </ModalV2>
  );
};

export default CancelSectionModal;
